import type { ReactNode } from 'react';
import { toast } from '@proerp/ui';

export type ToastTone = 'success' | 'danger' | 'info';

interface ToastApi {
  /**
   * `message` darf JSX sein, z. B. mit Ankern auf angelegte Belege. Achtung: Der
   * Toaster hängt außerhalb des Routers — router-`<Link>` geht dort nicht, nur `<a href>`.
   */
  push: (tone: ToastTone, message: ReactNode) => void;
  dismiss: () => void;
  /** Toast mit „Rückgängig"-Aktion (A3). `durationMs` = Undo-Fenster. */
  undo: (message: string, onUndo: () => void, durationMs?: number) => void;
}

/**
 * Toast-API (erp-design-guideline.md §6.6) auf sonner-Basis: oben rechts,
 * Erfolg/Info 4 s, Fehler bleiben bis zum Schließen stehen. Die Hook-Signatur
 * mit Selector bleibt aus Kompatibilität zur früheren Zustand-Implementierung
 * erhalten (`useToasts((state) => state.push)`).
 */
const api: ToastApi = {
  push: (tone, message) => {
    if (tone === 'success') toast.success(message, { duration: 4000 });
    else if (tone === 'info') toast.info(message, { duration: 4000 });
    else toast.error(message, { duration: Number.POSITIVE_INFINITY });
  },
  dismiss: () => toast.dismiss(),
  undo: (message, onUndo, durationMs = 5000) =>
    toast(message, {
      duration: durationMs,
      action: { label: 'Rückgängig', onClick: onUndo },
    }),
};

export function useToasts<T>(selector: (state: ToastApi) => T): T {
  return selector(api);
}
